import { Injectable, computed, signal } from '@angular/core';

import { claimsToAuthUser, decodeJwt, isExpired } from '@/core/auth/logic/jwt-decoder/jwt.decoder';
import { redirectUrlForRole } from '@/core/auth/logic/redirect/redirect';
import { AuthUser, UserRole } from '@/core/auth/types/auth.types';
import {
  type Curriculum,
  type GradeSlug,
  gradeNameToYear,
  gradeSlugToCurriculum,
  gradeYearToSlug,
} from '@/shared/utils/curriculum/curriculum';

export interface UserProfile {
  id: number;
  name: string;
  email: string;
  schoolId: number | null;
  schoolGroupName: string | null;
  classGroupId: number | null;
  gradeName: string | null;
  serieSlug?: GradeSlug | null;
}

@Injectable({ providedIn: 'root' })
export class AuthStore {
  private readonly accessTokenSignal = signal<string | null>(null);
  private readonly userProfileSignal = signal<UserProfile | null>(null);

  readonly accessToken = this.accessTokenSignal.asReadonly();
  readonly userProfile = this.userProfileSignal.asReadonly();
  readonly isAuthenticated = computed(() => this.authUser() !== null);
  readonly authUser = computed<AuthUser | null>(() => {
    const token = this.accessTokenSignal();
    if (!token) return null;
    const claims = decodeJwt(token);
    if (!claims || isExpired(claims)) return null;
    return claimsToAuthUser(claims);
  });
  readonly role = computed(() => this.authUser()?.role ?? null);
  readonly isTeacher = computed(() => this.role() === UserRole.Teacher);
  readonly isStudent = computed(() => this.role() === UserRole.Student);
  readonly gradeSlug = computed<GradeSlug | null>(() => {
    const profile = this.userProfileSignal();
    if (profile?.serieSlug) return profile.serieSlug;
    const grade = gradeNameToYear(profile?.gradeName ?? '');
    return grade !== null ? gradeYearToSlug(grade) : null;
  });
  readonly curriculum = computed<Curriculum | null>(() => {
    const slug = this.gradeSlug();
    return slug ? gradeSlugToCurriculum(slug) : null;
  });
  // Single place that knows which signals feed the post-login destination, so
  // callers don't each snapshot the right combination.
  readonly postLoginUrl = computed(() =>
    redirectUrlForRole(this.role() ?? UserRole.Student, this.gradeSlug()),
  );

  setSession(accessToken: string): void {
    this.accessTokenSignal.set(accessToken);
  }

  setUserProfile(profile: UserProfile | null): void {
    this.userProfileSignal.set(profile);
  }

  clear(): void {
    this.accessTokenSignal.set(null);
    this.userProfileSignal.set(null);
  }
}
